<template>
  <nav :class="{ scrolled: isScrolled }">
    <Container>
      <div class="nav-inner">
        <a href="#home" class="logo">
          <img src="@/assets/logo_new.png" alt="Deyoke Foods" />
        </a>

        <ul class="nav-links">
          <li v-for="link in links" :key="link.id">
            <a :href="`#${link.id}`">{{ link.label }}</a>
          </li>
        </ul>

        <div class="nav-actions">
          <Button href="#products" variant="outline-dark" size="sm">Shop</Button>
          <Button href="#footer" variant="primary" size="sm">Contact Us</Button>
        </div>

        <button
          class="menu-toggle"
          :class="{ active: showMenu }"
          @click="toggleMenu"
          aria-label="Toggle menu"
          :aria-expanded="showMenu"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </Container>

    <transition name="fade">
      <div v-if="showMenu" class="drawer-backdrop" @click="toggleMenu"></div>
    </transition>

    <transition name="slide">
      <div v-if="showMenu" class="drawer">
        <ul>
          <li v-for="link in links" :key="link.id">
            <a :href="`#${link.id}`" @click="toggleMenu">{{ link.label }}</a>
          </li>
        </ul>
        <div class="drawer-actions">
          <Button href="#products" variant="outline-dark" block @click="toggleMenu">Shop</Button>
          <Button href="#footer" variant="primary" block @click="toggleMenu">Contact Us</Button>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import Button from "@/components/Button/Button.vue"
import Container from "@/components/Container/Container.vue"
import { ref, onMounted, onUnmounted } from "vue"

const links = [
  { id: "home", label: "Home" },
  { id: "about", label: "About Us" },
  { id: "products", label: "Products" },
  { id: "testimonials", label: "Testimonials" },
  { id: "team", label: "Team" },
  { id: "faq", label: "FAQ" },
]

const isScrolled = ref(false)
const showMenu = ref(false)

const toggleMenu = () => {
  showMenu.value = !showMenu.value
  document.body.style.overflow = showMenu.value ? "hidden" : ""
}

const scrollHandler = () => {
  isScrolled.value = window.scrollY > 8
}

onMounted(() => {
  window.addEventListener("scroll", scrollHandler, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener("scroll", scrollHandler)
  document.body.style.overflow = ""
})
</script>

<style scoped>
nav {
  width: 100%;
  background: var(--primary-800);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: background var(--transition), box-shadow var(--transition);
}

nav.scrolled {
  background: rgba(5, 33, 29, 0.82);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-md);
}

.nav-inner {
  display: flex;
  height: var(--navbar-height);
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.logo {
  display: flex;
  align-items: center;
  width: 128px;
  flex-shrink: 0;
}

.logo img {
  width: 100%;
  object-fit: contain;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
  list-style: none;
}

.nav-links a {
  position: relative;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.85);
  font-family: var(--font-heading);
  font-size: 0.92rem;
  font-weight: 600;
  padding: 0.4rem 0;
  transition: color var(--transition);
}

.nav-links a::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--accent);
  border-radius: var(--radius-pill);
  transition: width var(--transition);
}

.nav-links a:hover {
  color: #fff;
}

.nav-links a:hover::after {
  width: 100%;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.nav-actions :deep(.btn-outline-dark) {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
}

.nav-actions :deep(.btn-outline-dark:hover) {
  background: rgba(255, 255, 255, 0.1);
  border-color: #fff;
}

/* Hamburger */
.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}

.menu-toggle span {
  width: 22px;
  height: 2px;
  background: #fff;
  border-radius: var(--radius-pill);
  transition: transform var(--transition), opacity var(--transition);
}

.menu-toggle.active span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.menu-toggle.active span:nth-child(2) {
  opacity: 0;
}

.menu-toggle.active span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* Drawer */
.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(5, 20, 18, 0.55);
  backdrop-filter: blur(2px);
  z-index: 998;
}

.drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(78%, 320px);
  background: var(--primary-800);
  z-index: 999;
  padding: calc(var(--navbar-height) + 1rem) 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  box-shadow: var(--shadow-lg);
}

.drawer ul {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.drawer a {
  text-decoration: none;
  color: #fff;
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 1.05rem;
}

.drawer-actions {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: auto;
}

.drawer-actions :deep(.btn-outline-dark) {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s var(--ease);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

@media (max-width: 1023px) {
  .nav-links,
  .nav-actions {
    display: none;
  }

  .menu-toggle {
    display: flex;
  }
}

@media (max-width: 767px) {
  .logo {
    width: 104px;
  }
}
</style>
